import Currencies from '../../Core/Enums/Currencies';
import { t } from 'i18next';
import { addVat } from './PaymentService';

const MapService = {
  getRoomTooltipInfo: room => {
    if (!room) return '';
    const priceLabel = t('price_per_month');
    const resolvedPriceLabel =
      priceLabel && priceLabel !== 'price_per_month' ? priceLabel : 'Price';
    const priceWithVat = addVat(room.SelfStoreMonthPrice).toFixed(2);

    return `${room.ItemSizeType.Name}\n ${resolvedPriceLabel}: ${priceWithVat} ${Currencies.getCodeByValue(room.SelfStoreMonthPriceUniCurrency)}`;
  },

  addFromPriceAndAvailableAmountToSizeTypes: (map, sizeTypes) => {
    const previousCalculatedContainerIds = [];
    // We create a copy of sizeTypes to avoid mutating the original array
    const updatedSizeTypes = sizeTypes.map(sizeType => ({ ...sizeType }));

    map.forEach((level, levelIndex) => {
      if (!Array.isArray(level)) return;
      level.forEach(row => {
        row.forEach(container => {
          if (!MapService.isContainer(container)) return;
          if (previousCalculatedContainerIds.includes(container.id)) return;

          //Add id to calculated list to avoid double counting rooms in same container
          previousCalculatedContainerIds.push(container.id);
          container.rooms.forEach(room => {
            const roomSizeType = room.ItemSizeType.Name;
            const roomPrice = room.SelfStoreMonthPrice;
            const roomCurrency = room.SelfStoreMonthPriceUniCurrency;
            const isRoomAvailable = MapService.isRoomAvailable(room);
            const roomSizeInSquareMeters = room.SelfStoreArea;
            const roomWidth = room.Width;
            const roomHeight = room.Height;
            const roomLength = room.Length;
            room.level = levelIndex;

            updatedSizeTypes.forEach(sizeType => {
              if (sizeType.sizeTypes.includes(roomSizeType)) {
                sizeType.levelAmount = levelIndex + 1;
                if (
                  (sizeType.fromPrice === 0 ||
                    roomPrice < sizeType.fromPrice) &&
                  isRoomAvailable
                ) {
                  sizeType.fromPrice = roomPrice;
                  sizeType.currency = roomCurrency;
                }
                if (isRoomAvailable) {
                  sizeType.availableAmount += 1;
                }
                sizeType.sizeInSquareMeters = roomSizeInSquareMeters;
                sizeType.width = roomWidth;
                sizeType.height = roomHeight;
                sizeType.length = roomLength;

                if (
                  (sizeType.levelPrice[levelIndex] == null ||
                    roomPrice < sizeType.levelPrice[levelIndex]) &&
                  isRoomAvailable &&
                  roomPrice > 0
                ) {
                  sizeType.levelPrice[levelIndex] = roomPrice;
                }
              }
            });
          });
        });
      });
    });
    return updatedSizeTypes;
  },

  generateAvailableRoomList: map => {
    const rooms = [];
    const previousCalculatedContainerIds = [];
    if (!Array.isArray(map)) return rooms;

    map.forEach((level, levelIndex) => {
      if (!Array.isArray(level)) return;
      level.forEach(row => {
        row.forEach(container => {
          if (!MapService.isContainer(container)) return;
          if (previousCalculatedContainerIds.includes(container.id)) return;
          previousCalculatedContainerIds.push(container.id);
          container.rooms.forEach(room => {
            if (MapService.isRoomAvailable(room)) {
              room.level = levelIndex;
              room.sizeType = room.ItemSizeType.Name;
              rooms.push(room);
            }
          });
        });
      });
    });
    return rooms;
  },

  //Check that a given room is available for booking based on its status and price
  isRoomAvailable: room => {
    return (
      room &&
      (room.IsDamaged === false || room.IsDamaged == null) &&
      room.ToLocation == null &&
      room.IsAtSelfSelfStoreLocation &&
      room.SelfStoreMonthPrice > 0
    );
  },

  //Check that a given cell is a container by type
  isContainer: cell => {
    return cell?.type === 'container';
  },

  // A Helper function to trim empty rows and columns from the map matrix to optimize rendering since js is slow.
  trimMatrix: matrix => {
    //Return an empty matrix if the input matrix is empty or only contains empty rows/columns to avoid errors in the trimming logic and unnecessary processing
    if (matrix.length === 0 || matrix[0].length === 0) return [[]];

    let minRow = 0,
      maxRow = matrix.length - 1;
    let minCol = 0,
      maxCol = matrix[0].length - 1;
    while (minRow <= maxRow && matrix[minRow].every(cell => cell === null))
      minRow++;
    while (maxRow >= minRow && matrix[maxRow].every(cell => cell === null))
      maxRow--;
    while (minCol <= maxCol) {
      const col = minCol;
      if (matrix.every(row => row[col] === null)) {
        minCol++;
      } else {
        break;
      }
    }
    while (maxCol >= minCol) {
      const col = maxCol;
      if (matrix.every(row => row[col] === null)) {
        maxCol--;
      } else {
        break;
      }
    }
    return matrix
      .slice(minRow, maxRow + 1)
      .map(row => row.slice(minCol, maxCol + 1));
  },

  // Fetch map data for a given location id from the backend API
  fetchMapDataByLocationId: async locationId => {
    const response = await fetch(
      `${process.env.REACT_APP_INTERNAL_API_URL}/api/selfstorageplanner/GetSelfStoragePlanWithRoomsInfo?selfStoragePlanId=${locationId}`
    );

    if (!response.ok) {
      throw new Error('Network response was not ok');
    }
    const data = await response.json();
    return data;
  },
};

export default MapService;
